import { CdkDragDrop, moveItemInArray } from '@angular/cdk/drag-drop';
import { BreakpointObserver, Breakpoints } from '@angular/cdk/layout';
import {
  AfterViewInit,
  ChangeDetectorRef,
  Component,
  computed,
  ElementRef,
  inject,
  input,
  OnChanges,
  OnDestroy,
  OnInit,
  output,
  signal,
  ViewChild
} from '@angular/core';
import { FormGroup } from '@angular/forms';
import { faGripDotsVertical } from '@fortawesome/pro-regular-svg-icons';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

import { PreviewDevice, PreviewDevices, QuestionOption, RatingType } from '@empathy/survey/base';
import { Question } from '@empathy/survey/base';

@Component({
  selector: 'empathy-rank-sort-question-view',
  templateUrl: './rank-sort-question-view.component.html',
  styleUrls: ['./rank-sort-question-view.component.scss', '_rank-sort-question-view-theme.component.scss']
})
export class RankSortQuestionViewComponent implements AfterViewInit, OnInit, OnChanges, OnDestroy {
  @ViewChild('rankSortContainer', { static: false }) rankSortContainer!: ElementRef;

  question = input<Question>();
  questionCtrl = input<FormGroup>();

  forceStop = output<boolean>();

  options$ = signal<Partial<QuestionOption>[]>([]);
  otherOption$ = computed(() => this.getOtherOption());
  ranks$ = signal<(Partial<QuestionOption> | null)[]>([]);
  showFeedback$ = computed(() => this.question().minRank <= this.ranks$().filter((r) => !!r).length);
  isNumeric$ = computed(() => this.question().ratingType === RatingType.numeric);
  isWeb$ = signal<boolean>(false);
  isMobileView$ = signal<boolean>(true);
  isOtherExclusiveSelected$ = computed(() => this.ranks$().some((r) => r && r.value === 'OTHER') && this.question().useExclusiveOtherInd);

  faGripDotsVertical = faGripDotsVertical;
  ratingType = RatingType;

  noneOption: Partial<QuestionOption> = {
    value: 'NONE',
    text: 'None'
  };

  private breakpointObserver = inject(BreakpointObserver);
  private cd = inject(ChangeDetectorRef);

  private destroyed = new Subject<void>();
  private webBreakpoints = [Breakpoints.Large, Breakpoints.XLarge];
  private readonly breakpoints = {
    xs: parseInt(Breakpoints.XSmall.match(/\d+/)?.[0] || '0', 10),
    sm: parseInt(Breakpoints.Small.match(/\d+/)?.[0] || '0', 10)
  };

  private resizeObserver!: ResizeObserver;

  get rankIds(): string[] {
    return this.ranks$().map((_, i) => `rank-${i}`);
  }

  ngOnInit(): void {
    this.breakpointObserver
      .observe([Breakpoints.XSmall, Breakpoints.Small, Breakpoints.Medium, Breakpoints.Large, Breakpoints.XLarge])
      .pipe(takeUntil(this.destroyed))
      .subscribe((result) => {
        let isCurrentWeb = false;

        for (const queryPoint of Object.keys(result.breakpoints)) {
          if (result.breakpoints[queryPoint]) {
            isCurrentWeb = this.webBreakpoints.includes(queryPoint);
            this.isMobileView$.set(!isCurrentWeb);
          }
        }
        this.isWeb$.set(isCurrentWeb);
      });

    if (this.question().useOtherInd) {
      this.questionCtrl()
        .get('otherText')
        ?.valueChanges.pipe(takeUntil(this.destroyed))
        .subscribe((value) => {
          if (!value) {
            this.forceStop.emit(true);
            return;
          }

          if (this.question().useExclusiveOtherInd) {
            this.forceStop.emit(false);
            return;
          }

          this.validateRankValues();
        });
    }
  }

  ngAfterViewInit(): void {
    this.resizeObserver = new ResizeObserver((entries) => {
      for (const entry of entries) {
        const width = entry.contentRect.width;
        const isMobile = this.isWeb$() ? width < PreviewDevices[PreviewDevice.Phone].width + 10 : width < this.breakpoints.xs;

        this.isMobileView$.set(isMobile);
        this.cd.detectChanges();
      }
    });

    if (this.rankSortContainer) {
      this.resizeObserver.observe(this.rankSortContainer.nativeElement);
    }
  }

  ngOnDestroy(): void {
    this.destroyed.next();
    this.destroyed.complete();

    if (this.resizeObserver) {
      this.resizeObserver.disconnect();
    }
  }

  ngOnChanges(): void {
    this.initializeRanks();
    this.initializeOptions();
  }

  moveItem(option: Partial<QuestionOption>, index: number): void {
    if (this.isOtherExclusiveSelected$()) {
      return;
    }

    let indexOfFirstNull = -1;
    this.ranks$.update((r) => {
      const copy = [...r];

      indexOfFirstNull = r.findIndex((element) => !element);
      if (indexOfFirstNull == -1) {
        return copy;
      }

      if (option.value === 'OTHER' && this.question().useExclusiveOtherInd) {
        this.updateRanksOnOtherDrop(copy);
      }

      copy[indexOfFirstNull] = option;
      return copy;
    });

    if (indexOfFirstNull !== -1) {
      this.removeOptions(index);
      this.mapRanksToQuestionCtrl();
      this.validateRankSort();
    }
  }

  onDropOption(event: CdkDragDrop<Partial<QuestionOption>[]>): void {
    if (event.previousContainer === event.container) {
      return;
    }

    const item = event.previousContainer.data[event.previousIndex];

    if (event.previousContainer.id.startsWith('rank')) {
      const fromIndex = parseInt(event.previousContainer.id.split('-')[1], 10);

      this.ranks$.update((r) => {
        const copy = [...r];
        copy[fromIndex] = null;
        return copy;
      });

      this.updateOptionsOnDrop(item);
    }

    this.mapRanksToQuestionCtrl();
    this.validateRankSort();
  }

  onCheckedNone(event: { checked: boolean }, option: Partial<QuestionOption>): void {
    if (event.checked) {
      this.questionCtrl().get('value').setValue([option.value]);
      this.initializeOptions();
      this.initializeRanks();
    } else {
      this.questionCtrl().get('value').setValue([]);
    }

    this.forceStop.emit(!event.checked);
  }

  onDropRank(event: CdkDragDrop<Partial<QuestionOption>[]>, index: number): void {
    const draggedItem = event.previousContainer.data[event.previousIndex];

    if (event.previousContainer.id.startsWith('rank')) {
      const fromIndex = parseInt(event.previousContainer.id.split('-')[1], 10);
      this.ranks$.update((ranks) => {
        const copy = [...ranks];
        moveItemInArray(copy, fromIndex, index);
        return copy;
      });
    } else {
      this.ranks$.update((current) => {
        const copy = [...current];
        const replaced = copy[index];

        copy[index] = draggedItem;

        if (replaced) {
          this.updateOptionsOnDrop(replaced);
        }

        if (draggedItem.value === 'OTHER' && this.question().useExclusiveOtherInd) {
          this.updateRanksOnOtherDrop(copy);
        }

        return copy;
      });

      if (event.previousContainer.id === 'options') {
        this.removeOptions(event.previousIndex);
      }
    }

    this.mapRanksToQuestionCtrl();
    this.validateRankSort();
  }

  private getOtherOption(): Partial<QuestionOption> | null {
    return this.question().useOtherInd
      ? ({
          value: 'OTHER',
          text: this.question().otherLabel
        } as Partial<QuestionOption>)
      : null;
  }

  private updateOptionsOnDrop(option: Partial<QuestionOption>): void {
    this.options$.update((opts) => {
      const emptyItemIndex = opts.findIndex((opt) => opt === null);
      if (emptyItemIndex !== -1) {
        opts[emptyItemIndex] = option;
      }

      return opts;
    });
  }

  private updateRanksOnOtherDrop(ranks: Partial<QuestionOption>[]): void {
    for (let i = 0; i < ranks.length; i++) {
      if (!!ranks[i] && ranks[i].value !== 'OTHER') {
        const item = ranks[i];
        ranks[i] = null;
        this.updateOptionsOnDrop(item);
      }
    }
  }

  private validateRankSort(): void {
    const otherInRanks = this.question().useOtherInd && this.ranks$().some((r) => r?.value === 'OTHER');

    if (this.question().useOtherInd && !otherInRanks) {
      const otherTextCtrl = this.questionCtrl().get('otherText');
      otherTextCtrl.clearValidators();
      otherTextCtrl.updateValueAndValidity();
      this.validateRankValues();
      return;
    }

    if (otherInRanks) {
      this.forceStop.emit(!this.questionCtrl().get('otherText')?.value);
      return;
    }

    this.validateRankValues();
  }

  private validateRankValues(): void {
    const selectedValues = this.ranks$().filter((r) => !!r).length;
    this.forceStop.emit(selectedValues < this.question().minRank || this.questionCtrl().get('value')?.value.includes(this.noneOption.value));
  }

  private mapRanksToQuestionCtrl(): void {
    this.questionCtrl()
      .get('value')
      ?.setValue(this.ranks$().map((rank: QuestionOption) => (!rank ? '' : rank.value)));
  }

  private initializeOptions(): void {
    const options: Partial<QuestionOption>[] = this.question().visibleOptions;

    if (this.question().useOtherInd) {
      options.splice(this.question().useOtherPosition, 0, this.otherOption$());
    }

    if (this.questionCtrl().get('value')?.value.length > 0) {
      const mappedOptions = options.map((opt) => (this.questionCtrl().get('value').value?.includes(opt.value) ? null : opt));
      mappedOptions.sort((a, b) => {
        if (a === null) {
          return 1;
        }
        if (b === null) {
          return -1;
        }

        return 0;
      });
      this.options$.set(mappedOptions);
    } else {
      this.options$.set(options);
    }
  }

  private initializeRanks(): void {
    if (this.questionCtrl().get('value')?.value.length > 0 && !this.questionCtrl().get('value').value.includes(this.noneOption.value)) {
      const ranks = this.questionCtrl()
        .get('value')
        .value.map((r: string) => {
          if (!r) {
            return null;
          }

          if (r === 'OTHER') {
            return this.otherOption$();
          }

          return this.question().visibleOptions.find((opt) => opt.value === r);
        });

      this.ranks$.set(ranks);
    } else {
      this.ranks$.set(Array(this.question().ratingType === RatingType.numeric ? this.question().ratingScale : this.question().ranks.length));
    }
  }

  private removeOptions(index: number): void {
    this.options$.update((opts) => {
      const copy = [...opts];
      copy.splice(index, 1);
      copy.push(null);
      return copy;
    });
  }
}
